import { useCallback, useEffect, useState } from "react";
import {
  decideAdminBooking,
  deleteAdminBooking,
  fetchAdminBookings,
} from "../api";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { id: "pending", label: "Függőben" },
  { id: "accepted", label: "Elfogadva" },
  { id: "declined", label: "Elutasítva" },
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.id, o.label]));

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function formatVisit(date, time) {
  if (!date) return "—";
  try {
    const day = new Intl.DateTimeFormat("hu-HU", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      weekday: "short",
    }).format(new Date(`${date}T00:00:00Z`));
    return `${day} ${time}`;
  } catch {
    return `${date} ${time}`;
  }
}

function IconRefresh() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

function BookingRow({ booking, isOpen, busy, onToggle, onDecide, onDelete }) {
  const isPending = booking.status === "pending";

  return (
    <>
      <tr className={isOpen ? "is-expanded" : undefined} onClick={onToggle}>
        <td>{formatVisit(booking.date, booking.time)}</td>
        <td>{booking.name || "—"}</td>
        <td>{booking.guests} fő</td>
        <td>{formatDate(booking.createdAt)}</td>
        <td>
          <span className={`admin-bookings-status admin-bookings-status--${booking.status}`}>
            {STATUS_LABELS[booking.status] || booking.status}
          </span>
        </td>
        <td onClick={(event) => event.stopPropagation()}>
          <div className="admin-bookings-actions">
            {isPending ? (
              <>
                <button
                  type="button"
                  className="admin-bookings-accept"
                  disabled={busy}
                  onClick={() => onDecide(booking, "accepted")}
                >
                  Elfogad
                </button>
                <button
                  type="button"
                  className="admin-bookings-decline"
                  disabled={busy}
                  onClick={() => onDecide(booking, "declined")}
                >
                  Elutasít
                </button>
              </>
            ) : (
              <button
                type="button"
                className="admin-orders-delete"
                disabled={busy}
                onClick={() => onDelete(booking)}
              >
                Törlés
              </button>
            )}
          </div>
        </td>
      </tr>
      {isOpen ? (
        <tr className="admin-orders-detail-row">
          <td colSpan={6}>
            <div className="admin-orders-detail">
              <p>
                <strong>E-mail:</strong> {booking.email || "—"}
              </p>
              <p>
                <strong>Telefon:</strong> {booking.phone || "—"}
              </p>
              <p>
                <strong>Megjegyzés:</strong> {booking.note || "—"}
              </p>
              <p>
                <strong>Nyelv:</strong> {booking.lang === "en" ? "angol" : "magyar"}
                {booking.decidedAt ? (
                  <>
                    {" · "}
                    <strong>Döntés:</strong> {formatDate(booking.decidedAt)}
                  </>
                ) : null}
              </p>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AdminBookings({ token }) {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(async () => {
    const data = await fetchAdminBookings(token, {
      status: statusFilter,
      q: debouncedQuery,
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    });
    setBookings(data.bookings || []);
    setTotal(data.total || 0);
    setPendingCount(data.pendingCount || 0);
  }, [token, statusFilter, debouncedQuery, page]);

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;

    async function run(silent) {
      if (!silent) setLoading(true);
      setError("");
      try {
        await load();
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni a foglalásokat.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run(false);
    const interval = window.setInterval(() => run(true), 30000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [token, load]);

  async function handleRefresh() {
    if (!token || refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      await load();
      setMessage("Frissítve.");
    } catch {
      setError("A frissítés sikertelen.");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDecide(booking, decision) {
    if (!token || busyId) return;
    const verb = decision === "accepted" ? "Elfogadod" : "Elutasítod";
    const when = formatVisit(booking.date, booking.time);
    if (
      !window.confirm(
        `${verb} ${booking.name} foglalását (${when}, ${booking.guests} fő)?\nA vendég e-mailt kap a döntésről.`,
      )
    ) {
      return;
    }

    setBusyId(booking.id);
    setError("");
    setMessage("");
    try {
      const data = await decideAdminBooking(token, booking.id, decision);
      setBookings((current) =>
        current.map((entry) => (entry.id === booking.id ? data.booking : entry)),
      );
      setPendingCount((current) => Math.max(0, current - 1));
      setMessage(
        decision === "accepted"
          ? "Foglalás elfogadva, a vendég megkapta a visszaigazolást."
          : "Foglalás elutasítva, a vendég értesítést kapott.",
      );
    } catch (err) {
      if (err.message === "booking_email_failed") {
        setError("Az e-mail küldése nem sikerült, a foglalás függőben maradt. Próbáld újra.");
      } else if (err.message === "booking_already_decided") {
        setError("Erről a foglalásról már döntöttek.");
        load().catch(() => {});
      } else {
        setError("A mentés sikertelen.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(booking) {
    if (!token || busyId) return;
    if (!window.confirm(`Biztosan törlöd ${booking.name} foglalását?`)) return;

    setBusyId(booking.id);
    setError("");
    setMessage("");
    try {
      await deleteAdminBooking(token, booking.id);
      setBookings((current) => current.filter((entry) => entry.id !== booking.id));
      setTotal((current) => Math.max(0, current - 1));
      if (expandedId === booking.id) setExpandedId(null);
      setMessage("Foglalás törölve.");
    } catch {
      setError("A törlés sikertelen.");
    } finally {
      setBusyId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Foglalások</h1>
          <p className="admin-dash-subtitle">
            Asztalfoglalások elfogadása vagy elutasítása.
            {pendingCount > 0 ? ` ${pendingCount} foglalás vár döntésre.` : ""}
          </p>
        </div>
      </header>

      <section className="admin-dash-toolbar">
        <label className="admin-dash-search">
          <span className="visually-hidden">Keresés</span>
          <input
            type="search"
            placeholder="Név, e-mail, telefon…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="admin-dash-toolbar-actions">
          <label className="admin-orders-filter">
            <span className="visually-hidden">Státusz</span>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
            >
              <option value="">Minden státusz</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`admin-dash-btn admin-dash-btn-outline admin-dash-btn-icon${refreshing ? " is-spinning" : ""}`}
            onClick={handleRefresh}
            disabled={refreshing || loading}
            aria-label="Frissítés"
            title="Frissítés"
          >
            <IconRefresh />
          </button>
        </div>
      </section>

      {error ? (
        <p className="newsletter-modal-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? <p className="admin-dash-toast">{message}</p> : null}

      <section className="admin-dash-table-card">
        {loading ? (
          <p className="admin-dash-empty">Betöltés…</p>
        ) : bookings.length === 0 ? (
          <p className="admin-dash-empty">
            Még nincs foglalás. Az új foglalások itt jelennek meg.
          </p>
        ) : (
          <>
            <div className="admin-dash-table-wrap">
              <table className="admin-dash-table admin-orders-table">
                <thead>
                  <tr>
                    <th>Időpont</th>
                    <th>Név</th>
                    <th>Létszám</th>
                    <th>Beérkezett</th>
                    <th>Státusz</th>
                    <th aria-label="Műveletek" />
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => {
                    const isOpen = expandedId === booking.id;
                    return (
                      <BookingRow
                        key={booking.id}
                        booking={booking}
                        isOpen={isOpen}
                        busy={busyId === booking.id}
                        onToggle={() => setExpandedId(isOpen ? null : booking.id)}
                        onDecide={handleDecide}
                        onDelete={handleDelete}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            <footer className="admin-dash-table-foot">
              <p>
                {total === 0
                  ? "0 foglalás"
                  : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} / ${total} foglalás`}
              </p>
              <div className="admin-dash-pagination">
                <button
                  type="button"
                  className="admin-dash-page-btn"
                  disabled={page <= 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  aria-label="Előző oldal"
                >
                  ‹
                </button>
                <span>
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="admin-dash-page-btn"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  aria-label="Következő oldal"
                >
                  ›
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </>
  );
}

export default AdminBookings;
